// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * StatusIndicator component for displaying asset status with colored dots and counts.
 * Used within asset cells to show ok/problem/error counts.
 */
// No React import needed for TypeScript
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { StatusIndicatorProps } from '@/inventory/components/overview/types';
import { formatNumber } from '@/utilities/number';
import { Box, IconButton, Typography } from '@mui/material';

const statusConfig: Record<
  AssetFunctionalStatus,
  { color: string; hoverColor: string }
> = {
  FUNCTIONAL: {
    color: '#4CAF50', // Green
    hoverColor: '#45a049',
  },
  NEEDS_REVIEW: {
    color: '#FFC107', // Orange/Amber
    hoverColor: '#FFA000',
  },
  NON_FUNCTIONAL: {
    color: '#F44336', // Red
    hoverColor: '#d32f2f',
  },
  SCRAPPED: {
    color: '#9E9E9E', // Grey
    hoverColor: '#757575',
  },
};

/**
 * StatusIndicator component
 */
export function StatusIndicator(props: StatusIndicatorProps) {
  const { status, count, onClick, size = 'medium', hideZero = false } = props;
  const config = statusConfig[status];
  const dotSize = size === 'small' ? 8 : 10;
  const isClickable = !!onClick && count > 0;

  if (hideZero && count === 0) {
    return null;
  }

  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 'fit-content',
      }}
    >
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          backgroundColor: config.color,
          flexShrink: 0,
          opacity: count === 0 ? 0.3 : 1,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          fontSize: size === 'small' ? '0.7rem' : '0.75rem',
          fontWeight: 500,
          color: 'text.primary',
          minWidth: '20px',
          textAlign: 'center',
          opacity: count === 0 ? 0.6 : 1,
        }}
      >
        {formatNumber(count)}
      </Typography>
    </Box>
  );

  if (isClickable) {
    return (
      <IconButton
        onClick={onClick}
        size="small"
        sx={{
          padding: 0.25,
          borderRadius: 1,
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&:focus': {
            outline: `2px solid ${config.color}`,
            outlineOffset: 1,
          },
        }}
        disabled={count === 0}
      >
        {content}
      </IconButton>
    );
  }

  return content;
}
